import React, { useState, useEffect } from 'react';
import { Project } from '../types';
import { getIntentCategories, getIntentColorForProject, getIntentLabelForProject } from '../utils/intentCategories';
import { loadAutoSelectSettings, saveAutoSelectSettings } from '../utils/autoSelectSettings';

export interface AutoSelectOptions {
  productIds: string[];
  intentCategories: string[];
  strategy: 'top-volume-only' | 'top-n-per-intent';
  topN?: number; // Default/global top N value
  intentTopN?: Record<string, number>; // Per-intent customization (intent -> topN)
}

interface AutoSelectModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (options: AutoSelectOptions) => void;
  project: Project;
  preselectedProductId?: string; // For single-product auto-select
}

const AutoSelectModal: React.FC<AutoSelectModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  project,
  preselectedProductId
}) => {
  const [selectedProductIds, setSelectedProductIds] = useState<string[]>([]);
  const [selectedIntentCategories, setSelectedIntentCategories] = useState<string[]>([]);
  const [strategy, setStrategy] = useState<'top-volume-only' | 'top-n-per-intent'>('top-volume-only');
  const [topN, setTopN] = useState<number>(3);
  const [intentTopN, setIntentTopN] = useState<Record<string, number>>({});

  const intentCategories = getIntentCategories(project);

  // Initialize with saved settings or defaults
  useEffect(() => {
    if (isOpen && project) {
      const savedSettings = loadAutoSelectSettings();
      
      // Initialize products
      if (preselectedProductId) {
        // Single product mode - only select that product
        setSelectedProductIds([preselectedProductId]);
      } else {
        // All products mode - use saved or all products
        setSelectedProductIds(savedSettings?.productIds || project.products.map(p => p.id));
      }

      // Initialize intents - use saved or all intents
      setSelectedIntentCategories(savedSettings?.intentCategories || intentCategories);

      // Initialize strategy
      setStrategy(savedSettings?.strategy || 'top-volume-only');
      setTopN(savedSettings?.topN || 3);
      setIntentTopN(savedSettings?.intentTopN || {});
    }
  }, [isOpen, project, preselectedProductId, intentCategories]);

  // Handle per-intent N value changes
  const handleIntentTopNChange = (intent: string, value: number) => {
    const numValue = Math.max(1, Math.min(10, value || 1));
    if (numValue === topN) {
      // If it matches the default, remove from customizations
      setIntentTopN(prev => {
        const updated = { ...prev };
        delete updated[intent];
        return updated;
      });
    } else {
      // Store the custom value
      setIntentTopN(prev => ({
        ...prev,
        [intent]: numValue
      }));
    }
  };

  // Get the effective topN for an intent (custom or default)
  const getIntentTopN = (intent: string): number => {
    return intentTopN[intent] ?? topN;
  };

  if (!isOpen) return null;

  const handleProductToggle = (productId: string) => {
    setSelectedProductIds(prev =>
      prev.includes(productId)
        ? prev.filter(id => id !== productId)
        : [...prev, productId]
    );
  };

  const handleIntentToggle = (intent: string) => {
    setSelectedIntentCategories(prev =>
      prev.includes(intent)
        ? prev.filter(i => i !== intent)
        : [...prev, intent]
    );
  };

  const handleSelectAllProducts = () => {
    if (preselectedProductId) {
      // In single product mode, only allow that product
      setSelectedProductIds([preselectedProductId]);
    } else {
      setSelectedProductIds(project.products.map(p => p.id));
    }
  };

  const handleDeselectAllProducts = () => {
    if (!preselectedProductId) {
      setSelectedProductIds([]);
    }
  };

  const handleSelectAllIntents = () => {
    setSelectedIntentCategories([...intentCategories]);
  };

  const handleDeselectAllIntents = () => {
    setSelectedIntentCategories([]);
  };

  const handleConfirm = () => {
    if (selectedProductIds.length === 0) {
      alert('Please select at least one product');
      return;
    }
    if (selectedIntentCategories.length === 0) {
      alert('Please select at least one intent category');
      return;
    }
    if (strategy === 'top-n-per-intent' && (topN < 1 || topN > 10)) {
      alert('Top N must be between 1 and 10');
      return;
    }

    const options: AutoSelectOptions = {
      productIds: selectedProductIds,
      intentCategories: selectedIntentCategories,
      strategy,
      topN: strategy === 'top-n-per-intent' ? topN : undefined,
      intentTopN: strategy === 'top-n-per-intent' && Object.keys(intentTopN).length > 0 ? intentTopN : undefined
    };

    // Save settings for next time
    saveAutoSelectSettings(options);

    onConfirm(options);
    onClose();
  };

  const allProductsSelected = preselectedProductId
    ? selectedProductIds.includes(preselectedProductId)
    : selectedProductIds.length === project.products.length;
  const allIntentsSelected = selectedIntentCategories.length === intentCategories.length;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-primary-900">
            Auto Select Options
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl"
          >
            ×
          </button>
        </div>

        <div className="space-y-6">
          {/* Product Selection */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-semibold text-primary-900">
                Select Products
                {preselectedProductId && (
                  <span className="text-sm font-normal text-gray-500 ml-2">
                    (Single product mode)
                  </span>
                )}
              </h3>
              {!preselectedProductId && (
                <div className="flex space-x-2">
                  <button
                    onClick={handleSelectAllProducts}
                    className="text-sm text-accent-500 hover:text-accent-600"
                    disabled={allProductsSelected}
                  >
                    Select All
                  </button>
                  <span className="text-gray-300">|</span>
                  <button
                    onClick={handleDeselectAllProducts}
                    className="text-sm text-gray-600 hover:text-gray-700"
                    disabled={selectedProductIds.length === 0}
                  >
                    Deselect All
                  </button>
                </div>
              )}
            </div>
            <div className="border border-gray-200 rounded-lg p-3 max-h-48 overflow-y-auto">
              <div className="space-y-2">
                {project.products.map(product => {
                  const isSelected = selectedProductIds.includes(product.id);
                  const isDisabled = preselectedProductId !== undefined && product.id !== preselectedProductId;
                  return (
                    <label
                      key={product.id}
                      className={`flex items-center space-x-2 p-2 rounded hover:bg-gray-50 ${
                        isDisabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={(e) => {
                          e.stopPropagation();
                          handleProductToggle(product.id);
                        }}
                        disabled={isDisabled}
                        className="rounded border-gray-300 text-accent-500 focus:ring-accent-500 cursor-pointer"
                      />
                      <span className="text-sm text-gray-700">{product.name}</span>
                      {isDisabled && (
                        <span className="text-xs text-gray-400">(locked)</span>
                      )}
                    </label>
                  );
                })}
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              {selectedProductIds.length} of {project.products.length} products selected
            </p>
          </div>

          {/* Intent Selection */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-semibold text-primary-900">Select Intent Categories</h3>
              <div className="flex space-x-2">
                <button
                  onClick={handleSelectAllIntents}
                  className="text-sm text-accent-500 hover:text-accent-600"
                  disabled={allIntentsSelected}
                >
                  Select All
                </button>
                <span className="text-gray-300">|</span>
                <button
                  onClick={handleDeselectAllIntents}
                  className="text-sm text-gray-600 hover:text-gray-700"
                  disabled={selectedIntentCategories.length === 0}
                >
                  Deselect All
                </button>
              </div>
            </div>
            <div className="border border-gray-200 rounded-lg p-3 max-h-48 overflow-y-auto">
              <div className="grid grid-cols-2 gap-2">
                {intentCategories.map(intent => {
                  const isSelected = selectedIntentCategories.includes(intent);
                  const color = getIntentColorForProject(project, intent);
                  const label = getIntentLabelForProject(project, intent);
                  const bgColor = color.split(' ')[0];
                  return (
                    <label
                      key={intent}
                      className="flex items-center space-x-2 p-2 rounded hover:bg-gray-50 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={(e) => {
                          e.stopPropagation();
                          handleIntentToggle(intent);
                        }}
                        className="rounded border-gray-300 text-accent-500 focus:ring-accent-500 cursor-pointer"
                      />
                      <div className={`w-3 h-3 rounded-full ${bgColor}`}></div>
                      <span className="text-sm text-gray-700">{label}</span>
                    </label>
                  );
                })}
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              {selectedIntentCategories.length} of {intentCategories.length} intents selected
            </p>
          </div>

          {/* Selection Strategy */}
          <div>
            <h3 className="text-lg font-semibold text-primary-900 mb-3">Selection Strategy</h3>
            <div className="space-y-3">
              <label className="flex items-center space-x-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                <input
                  type="radio"
                  name="strategy"
                  value="top-volume-only"
                  checked={strategy === 'top-volume-only'}
                  onChange={() => setStrategy('top-volume-only')}
                  className="text-accent-500 focus:ring-accent-500"
                />
                <div>
                  <span className="text-sm font-medium text-primary-900">Top Volume Only</span>
                  <p className="text-xs text-gray-500">Select the highest volume keyword from each intent category</p>
                </div>
              </label>
              <label className="flex items-center space-x-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                <input
                  type="radio"
                  name="strategy"
                  value="top-n-per-intent"
                  checked={strategy === 'top-n-per-intent'}
                  onChange={() => setStrategy('top-n-per-intent')}
                  className="text-accent-500 focus:ring-accent-500"
                />
                <div className="flex-1">
                  <div className="flex items-center space-x-3">
                    <span className="text-sm font-medium text-primary-900">Top N per Intent</span>
                    {strategy === 'top-n-per-intent' && (
                      <div className="flex items-center space-x-2">
                        <input
                          type="number"
                          min="1"
                          max="10"
                          value={topN}
                          onChange={(e) => setTopN(parseInt(e.target.value) || 1)}
                          className="w-16 px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-accent-500 focus:border-accent-500"
                        />
                        <span className="text-xs text-gray-500">keywords per intent</span>
                      </div>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Select the top N highest volume keywords from each intent category</p>
                </div>
              </label>
            </div>
          </div>

          {/* Per-Intent Customization (only shown for Top N per Intent strategy) */}
          {strategy === 'top-n-per-intent' && selectedIntentCategories.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-semibold text-primary-900">
                  Customize Per Intent
                </h3>
                <button
                  onClick={() => setIntentTopN({})}
                  className="text-sm text-gray-600 hover:text-gray-700"
                  disabled={Object.keys(intentTopN).length === 0}
                >
                  Reset All to Default
                </button>
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-3">
                <p className="text-xs text-blue-800">
                  <span className="font-medium">Default:</span> {topN} keyword{topN !== 1 ? 's' : ''} per intent. 
                  Customize individual intents below, or leave blank to use the default.
                </p>
              </div>
              <div className="border border-gray-200 rounded-lg p-3 max-h-64 overflow-y-auto">
                <div className="space-y-3">
                  {selectedIntentCategories.map(intent => {
                    const color = getIntentColorForProject(project, intent);
                    const label = getIntentLabelForProject(project, intent);
                    const bgColor = color.split(' ')[0];
                    const customValue = getIntentTopN(intent);
                    const isCustomized = intent in intentTopN;
                    return (
                      <div
                        key={intent}
                        className="flex items-center justify-between p-2 rounded hover:bg-gray-50"
                      >
                        <div className="flex items-center space-x-2 flex-1">
                          <div className={`w-3 h-3 rounded-full ${bgColor}`}></div>
                          <span className="text-sm text-gray-700 flex-1">{label}</span>
                          {isCustomized && (
                            <span className="text-xs text-accent-500 font-medium">(custom)</span>
                          )}
                        </div>
                        <div className="flex items-center space-x-2">
                          <label className="text-xs text-gray-600">Top</label>
                          <input
                            type="number"
                            min="1"
                            max="10"
                            value={customValue}
                            onChange={(e) => handleIntentTopNChange(intent, parseInt(e.target.value) || 1)}
                            className="w-16 px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-accent-500 focus:border-accent-500"
                          />
                          <label className="text-xs text-gray-600">keyword{customValue !== 1 ? 's' : ''}</label>
                          {isCustomized && (
                            <button
                              onClick={() => handleIntentTopNChange(intent, topN)}
                              className="ml-2 text-xs text-gray-500 hover:text-gray-700 underline"
                              title="Reset to default"
                            >
                              Reset
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* Preview Summary */}
          <div className="bg-gray-50 rounded-lg p-3">
            <p className="text-sm text-gray-700">
              <span className="font-medium">Preview:</span> Will auto-select keywords for{' '}
              <span className="font-semibold">{selectedProductIds.length}</span> product
              {selectedProductIds.length !== 1 ? 's' : ''} across{' '}
              <span className="font-semibold">{selectedIntentCategories.length}</span> intent
              {selectedIntentCategories.length !== 1 ? 's' : ''} using{' '}
              <span className="font-semibold">
                {strategy === 'top-volume-only' 
                  ? 'top volume only' 
                  : Object.keys(intentTopN).length > 0
                    ? 'custom per intent'
                    : `top ${topN} per intent`}
              </span>
              .
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirm}
              className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 transition-colors"
            >
              Apply Auto Select
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AutoSelectModal;

